/*
 *   Copyright (c) 2026 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  TicketErrorCode,
  IParsedTicketError,
} from "../pages/Costume/components/SelectTicketsComp/types";

/**
 * Known error codes returned by the GEMS ticketing backend (PRD #719).
 */
export const TICKET_ERROR_CODES = {
  BOX_LIMIT_EXCEEDED: "BOX_LIMIT_EXCEEDED",
  SOLD_OUT: "SOLD_OUT",
  FETE_NOT_AVAILABLE: "FETE_NOT_AVAILABLE",
  TIER_NOT_FOUND: "TIER_NOT_FOUND",
  HOLD_EXPIRED: "HOLD_EXPIRED",
  INVALID_STATE: "INVALID_STATE",
  QUANTITY_INCREASE_NOT_ALLOWED: "QUANTITY_INCREASE_NOT_ALLOWED",
  LOCKED_BY_OTHER_TX: "LOCKED_BY_OTHER_TX",
  EMAIL_REQUIRED: "EMAIL_REQUIRED",
  NOT_FOUND: "NOT_FOUND",
  INVALID_REQUEST: "INVALID_REQUEST",
  UPSTREAM_UNAVAILABLE: "UPSTREAM_UNAVAILABLE",
} as const;

/**
 * Regex matching the backend error prefix code before the first colon:
 * e.g. "BOX_LIMIT_EXCEEDED: Maximum 4 tickets per box reached"
 */
export const TICKET_ERROR_REGEX = /^([A-Z0-9_]+):\s*([\s\S]*)$/;

/**
 * Extract raw message string from an Apollo / GraphQL / Axios / Error object or string.
 */
export const extractErrorMessage = (error: any): string => {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (Array.isArray(error?.graphQLErrors) && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0]?.message || "";
  }
  if (
    Array.isArray(error?.networkError?.result?.errors) &&
    error.networkError.result.errors.length > 0
  ) {
    return error.networkError.result.errors[0]?.message || "";
  }
  if (error?.message) return error.message;
  return String(error);
};

/**
 * Action-oriented friendly message mapping based on PRD #719 guide.
 */
export const getTicketErrorUserMessage = (
  code: TicketErrorCode | null,
  fallbackMessage?: string
): string => {
  switch (code) {
    case TICKET_ERROR_CODES.BOX_LIMIT_EXCEEDED:
      return (
        fallbackMessage ||
        "You have reached the maximum ticket limit per costume for this event. Please lower your quantity."
      );
    case TICKET_ERROR_CODES.SOLD_OUT:
      return (
        fallbackMessage ||
        "Not enough tickets remaining for the selected tier. Please refresh and select another tier."
      );
    case TICKET_ERROR_CODES.FETE_NOT_AVAILABLE:
      return (
        fallbackMessage ||
        "This event is no longer available for ticket sales through GEMS. Please refresh the event list."
      );
    case TICKET_ERROR_CODES.TIER_NOT_FOUND:
      return (
        fallbackMessage ||
        "The selected ticket tier no longer exists. Please refresh the event list."
      );
    case TICKET_ERROR_CODES.HOLD_EXPIRED:
      return (
        fallbackMessage ||
        "Your ticket reservation has expired. Nothing was charged. Please reserve your tickets again."
      );
    case TICKET_ERROR_CODES.INVALID_STATE:
      return (
        fallbackMessage ||
        "This ticket order is currently not in a valid state. Please refresh your orders."
      );
    case TICKET_ERROR_CODES.QUANTITY_INCREASE_NOT_ALLOWED:
      return (
        fallbackMessage ||
        "Ticket quantity cannot be increased on an existing reservation. Please reserve additional tickets separately."
      );
    case TICKET_ERROR_CODES.LOCKED_BY_OTHER_TX:
      return (
        fallbackMessage ||
        "A payment for these tickets is still in progress. If you closed the payment page, try again in about 15 minutes."
      );
    case TICKET_ERROR_CODES.EMAIL_REQUIRED:
      return (
        fallbackMessage ||
        "An email address is required in your profile to complete ticket purchases. Please update your profile."
      );
    case TICKET_ERROR_CODES.NOT_FOUND:
      return (
        fallbackMessage ||
        "The requested ticket order or costume box could not be found. Please refresh."
      );
    case TICKET_ERROR_CODES.INVALID_REQUEST:
      return (
        fallbackMessage ||
        "Invalid ticket request details. Please check your selections and try again."
      );
    case TICKET_ERROR_CODES.UPSTREAM_UNAVAILABLE:
      return (
        fallbackMessage ||
        "Tickets are temporarily unavailable. You can continue with your costume purchase and try tickets again later."
      );
    default:
      return fallbackMessage || "An unexpected error occurred. Please try again.";
  }
};

/**
 * Parses GraphQL and API errors into a standardized structured ticket error.
 * Extracts prefix code e.g. "BOX_LIMIT_EXCEEDED: ..." if present.
 * If no code prefix exists (e.g. costume validation error), code is null.
 */
export const parseTicketError = (error: any): IParsedTicketError => {
  const rawMessage = extractErrorMessage(error);
  const match = rawMessage.match(TICKET_ERROR_REGEX);

  if (match) {
    const code = match[1] as TicketErrorCode;
    const cleanMessage = match[2]?.trim() || rawMessage;
    return {
      code,
      message: cleanMessage,
      rawMessage,
    };
  }

  return {
    code: null,
    message: rawMessage,
    rawMessage,
  };
};

/**
 * Utility predicates for ticket error handling
 */
export const isTicketUpstreamUnavailable = (error: any): boolean => {
  return parseTicketError(error).code === TICKET_ERROR_CODES.UPSTREAM_UNAVAILABLE;
};

export const isTicketHoldExpired = (error: any): boolean => {
  return parseTicketError(error).code === TICKET_ERROR_CODES.HOLD_EXPIRED;
};

export const isTicketBoxLimitExceeded = (error: any): boolean => {
  return parseTicketError(error).code === TICKET_ERROR_CODES.BOX_LIMIT_EXCEEDED;
};

export const isTicketSoldOut = (error: any): boolean => {
  return parseTicketError(error).code === TICKET_ERROR_CODES.SOLD_OUT;
};
